import { pdsUtils } from '../../pds-utils';
import { HtmlContentHeuristic } from '../../interfaces/html-content-heuristic';
import { HtmlContentProperty } from '../../interfaces/html-content-property';
import { HtmlContentTarget } from '../../interfaces/html-content-target';
import { HtmlContentSection } from './html-content-section';

// section break heuristics
import { breakOnAll } from './html-content-heuristics/all';
import { breakOnBackgroundChange } from './html-content-heuristics/background-change';
import { breakOnBold } from './html-content-heuristics/bold';
import { breakOnFontSizeIncrease } from './html-content-heuristics/font-size-increase';
import { breakOnTableChange } from './html-content-heuristics/table-change';
import { breakOnTooManyCharacters } from './html-content-heuristics/too-many-characters';

// section break properties
import { background } from './html-content-properties/background';
import { fontSize } from './html-content-properties/font-size';
import { fontWeight } from './html-content-properties/font-weight';
import { tableAncestor } from './html-content-properties/table-ancestor';
import { textLength } from './html-content-properties/text-length';
import { NodeGroupingMode } from './node-grouping-mode';

const firstTextNodeReason = 'first text node';
const noHeuristicsReason = 'no heuristics were supplied';

// tree walkers in some older browsers don't accept { acceptNode: (node: Node) => number }, they only accept (node: Node) => number.
const nodeFilterFunction = (node: Node): number => {
  // only track text nodes with text in them that are attached to HTML Elements
  if (node.textContent != null &&
      node.textContent.trim().length > 0 &&
      node.parentNode instanceof HTMLElement) {
    return NodeFilter.FILTER_ACCEPT;
  }

  return NodeFilter.FILTER_SKIP;
};

// we need to combine those two formats into one
const nodeFilter: NodeFilter = nodeFilterFunction as any;
nodeFilter.acceptNode = nodeFilterFunction as any;

export class HtmlContentSectionsEngine {
  // expose bundled rules through the toolkit
  static sectionBreakHeuristics = {
    breakOnAll,
    breakOnBackgroundChange,
    breakOnBold,
    breakOnFontSizeIncrease,
    breakOnTableChange,
    breakOnTooManyCharacters
  };

  // expose bundled properties through the toolkit
  static sectionBreakProperties = {
    background,
    fontSize,
    fontWeight,
    tableAncestor,
    textLength
  };

  static NodeGroupingMode: typeof NodeGroupingMode = NodeGroupingMode;

  // evaluate all registered heuristics
  static evaluateHeuristics(heuristics: Array<HtmlContentHeuristic>, nextProperties: Record<string, any>, prevProperties: Record<string, any>): Array<string> {
    return heuristics
      // filter out heuristics that don't indicate a section break is needed
      .filter((heuristic: HtmlContentHeuristic) => heuristic.evaluate(nextProperties, prevProperties))
      // get the reason for each heuristic that indicated a section break is needed
      .map((heuristic: HtmlContentHeuristic) => heuristic.reason);
  }

  // combine all properties together into a single Record object
  static evaluateProperties(properties: Array<HtmlContentProperty>, target: HtmlContentTarget, style: CSSStyleDeclaration, section: HtmlContentSection): Record<string, any> {
    return properties
      // combine all the properties together into a single object
      .reduce((allProperties: Record<string, any>, property: HtmlContentProperty) => {
        if (typeof property.value === 'string' || typeof property.value === 'number') {
          // if the property is a style key, just get it from the style object
          allProperties[property.name] = style[property.value];
        } else {
          // otherwise, get the value from the function
          allProperties[property.name] = property.value(target, style, section);
        }

        return allProperties;
      }, {});
  }

  // check whether the current node contains elements that are part of a prior section
  static nodeContainsPreviousElements(node: HTMLElement | Node, sections: Array<HtmlContentSection>, limit: HTMLElement): boolean {
    // if the node is not an element, it cannot contain other elements so we can immediately return false
    if (!(node instanceof HTMLElement)) {
      return false;
    }

    let containsAlreadyProcessedElement = false;

    // check if this is an ancestor of an already processed element
    for (let section of sections) {
      for (let i = 0; i < section.nodes.length && !containsAlreadyProcessedElement; i++) {
        // check each previously selected element to see if it is a descendent of this new element
        let sectionAncestor: HTMLElement | null = section.nodes[i] instanceof HTMLElement
          ? section.nodes[i] as HTMLElement
          : section.nodes[i].parentNode as HTMLElement;

        while (!containsAlreadyProcessedElement && sectionAncestor && sectionAncestor !== limit) {
          containsAlreadyProcessedElement = sectionAncestor === node;
          sectionAncestor = sectionAncestor.parentElement;
        }
      }

      // if the element contains an existing element, stop processing, we have our answer
      if (containsAlreadyProcessedElement) {
        break;
      }
    }

    return containsAlreadyProcessedElement;
  }

  // check whether this node has already been included in another section
  static nodeHasBeenProcessed(node: Node, sections: Array<HtmlContentSection>, limit: HTMLElement): boolean {
    let alreadyProcessed = false;

    // check each ancestor of the node to see if it has already been included in a section
    let ancestor: HTMLElement | null = node.parentNode as HTMLElement | null;
    while (!alreadyProcessed && ancestor && ancestor !== limit) {
      // copy due to the closure
      let target: HTMLElement = ancestor;

      alreadyProcessed = sections.some((section: HtmlContentSection) => section.includesElement(target));

      ancestor = ancestor.parentElement;
    }

    return alreadyProcessed;
  }

  /* gets the target node and element.
   *  The target node is the group of text to process.
   *  The target element is the element to pull style info from.*/
  static getTarget(textNode: Node, groupingMode: NodeGroupingMode, limit: HTMLElement): HtmlContentTarget {
    // create a function so we can recursively fall back to looser grouping modes if the stricter mode fails
    let getTargetNode = (selectorMode: NodeGroupingMode): HTMLElement | Node => {
      switch (selectorMode) {
        case NodeGroupingMode.Block:
          // if there is no block parent, fall back to element grouping mode
          return this.getBlockParent(textNode, limit) || getTargetNode(NodeGroupingMode.Element);
        case NodeGroupingMode.Element:
          // of there is no parent element, fall back to just selecting the text node
          return textNode.parentNode as HTMLElement | null || getTargetNode(NodeGroupingMode.Text);
        case NodeGroupingMode.Text:
        default:
          // text grouping just returns the individual text node
          return textNode;
      }
    };

    let node: HTMLElement | Node = getTargetNode(groupingMode);

    // if the node is an element, then we can pull the style directly from the element. Otherwise use the text node's parent
    let element = node instanceof HTMLElement
      ? node
      : node.parentNode as HTMLElement | null || limit;

    return { node, element, container: limit };
  }

  // gets the closest block (i.e. non-inline) parent
  static getBlockParent(node: Node, limit: HTMLElement): HTMLElement | null {
    let element = node.parentNode as HTMLElement | null;
    while (element && element !== limit) {
      if (!pdsUtils.isInline(element)) {
        break;
      }

      element = element.parentElement;
    }

    // if the limit is not a block, don't return any block parent
    if (element === limit && pdsUtils.isInline(element)) {
      element = null;
    }

    return element;
  }

  /**
   * this will return whether or not the element is takes up space on the page
   *  An element is visible if it has width, height, and a client rectangle. If it is missing any
   *  of those requirements, then it was not drawn on the page, and is as such not displayed.
   *  Examples:
   *   An element with `display: none;` will return false.
   *   An element whose parent has `display: none;` will return false.
   *   An element with `visibility: hidden;` will return false.
   *   An element whose parent has `visibility: hidden;` will return false.
   *   An element that is not on the screen but will display when scrolled to will return true.
   *   An element that is positioned outside of the containing scroll box will return true.
   * @param element
   * @returns {boolean}
   */
  static isVisible(element: HTMLElement, style: CSSStyleDeclaration): boolean {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length) && style.visibility === 'visible';
  }

  constructor(private heuristics: Array<HtmlContentHeuristic>) {}

  // processes all non-empty text nodes in the container and divides them into sections, based on the heuristics passed into the engine
  //  selectBlocks determines whether the sections are selected by the closest element or closest block element
  parse(container: HTMLElement, maxRight: number, maxBottom: number, groupingMode: NodeGroupingMode): Array<HtmlContentSection> {
    let parsedSections: Array<HtmlContentSection> = [];

    // if no heuristics were supplied, we can't break the content up into sections - just return a single section
    if (this.heuristics.length === 0) {
      let onlySection = new HtmlContentSection(container, maxRight, maxBottom, [noHeuristicsReason]);
      onlySection.addNode(container);
      return [onlySection];
    }

    // make sure we process each property once
    let properties = this.getUniquePropertiesList();

    // this walker will go through each text node that has content
    let walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, nodeFilter, false);

    let section: HtmlContentSection | undefined;
    let previousElementProperties: Record<string, any> | undefined;

    let textNode: Node | null;
    while (textNode = walker.nextNode()) {
      // get the target node/element so that we can process it
      let target: HtmlContentTarget = HtmlContentSectionsEngine.getTarget(textNode, groupingMode, container);
      // getComputedStyle is slow, evaluate it once per element
      const style = getComputedStyle(target.element);

      // skip text nodes that are not visible
      if (!HtmlContentSectionsEngine.isVisible(target.element, style)) {
        continue;
      }

      // if there currently is no section, start a new one and skip the rest of the process
      if (section == null || previousElementProperties == null) {
        section = new HtmlContentSection(container, maxRight, maxBottom, [firstTextNodeReason]);
        section.addNode(target.node);
        parsedSections.push(section);
        previousElementProperties = HtmlContentSectionsEngine.evaluateProperties(properties, target, style, section);
        continue;
      }

      // if this is a descendent of an already processed element, no need to include it again
      if (HtmlContentSectionsEngine.nodeHasBeenProcessed(textNode, parsedSections, container)) {
        continue;
      }

      // if this node contains previously selected elements, it's too big - fall back to a looser grouping mode and check again
      let thisGroupingMode = groupingMode;
      while (thisGroupingMode > NodeGroupingMode.Text && HtmlContentSectionsEngine.nodeContainsPreviousElements(target.node, parsedSections, container)) {
        thisGroupingMode--;
        target = HtmlContentSectionsEngine.getTarget(textNode, thisGroupingMode, container);
      }

      // process the properties for the current element
      let currentElementProperties = HtmlContentSectionsEngine.evaluateProperties(properties, target, style, section);

      // if one or more of the section break heuristics indicates there is likely a section break here...
      let breakReasons = HtmlContentSectionsEngine.evaluateHeuristics(this.heuristics, currentElementProperties, previousElementProperties);
      if (breakReasons.length) {
        // ...create a new section
        section = new HtmlContentSection(container, maxRight, maxBottom, breakReasons);
        parsedSections.push(section);
      }

      // keep track of the properties for this element so we can detect changes in properties
      previousElementProperties = currentElementProperties;

      // add the current node to the current section
      section.addNode(target.node);
    }

    return parsedSections;
  }

  // combine property lists from heuristics together to form one list of unique properties
  getUniquePropertiesList(): Array<HtmlContentProperty> {
    return this.heuristics
      // get just the properties from each heuristic
      .map((heuristic: HtmlContentHeuristic) => heuristic.properties)
      // combine all properties together into one list
      .reduce((uniqueProperties: Array<HtmlContentProperty>, properties: Array<HtmlContentProperty>) => {
        // filter out any properties that have already been added to the list
        let newProperties = properties.filter((property: HtmlContentProperty) => !uniqueProperties.includes(property));

        // add the new properties onto the unique list
        return uniqueProperties.concat(newProperties);
      }, []);
  }
}
